import { useState, useEffect } from "react";
import { SelectedFilter } from "./filter-template";
import { Chip } from "ui/chip";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { MultiSelectTags } from "@/components/ui/multi-select-tags";
import { cn } from "@/lib/utils";

interface FilterProps<T> {
  label: string;
  value: T;
  onChange(value: T): void;
  onRemove?(defaultValue: T): void;
  placeholder?: string;
  formatLabel?(value: T): string;
  icon?: string;
}

export function TextFilter(props: FilterProps<string>) {
  const [value, setValue] = useState(props.value);

  const label = (
    <>
      <span className="font-semibold">{props.label}:</span>
      {props.value && (
        <>
          <span className="font-normal">{props.value}</span>
        </>
      )}
    </>
  );

  return (
    <SelectedFilter
      value={value}
      label={label}
      icon={props.icon}
      onChange={props.onChange}
      onRemove={props.onRemove ? () => props.onRemove!("") : undefined}
      hasValue={!!value}
      renderEditing={() => (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-neutral-700 block">
            {props.label}
          </label>
          <Input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            placeholder={props.placeholder}
            className="h-7 text-xs border-neutral-200 focus:border-neutral-400"
          />
        </div>
      )}
    />
  );
}

export function SelectFilter(
  props: FilterProps<string> & {
    options: Array<{
      label: string;
      value: string;
    }>;
  }
) {
  const [value, setValue] = useState(props.value);

  const selectedOption = props.options.find((opt) => opt.value === value);

  let label: React.ReactNode;
  if (props.formatLabel) {
    const formattedLabel = props.formatLabel(value);
    const parts = formattedLabel.split(":");
    if (parts.length === 2) {
      label = (
        <>
          <span className="font-semibold">{parts[0].trim()}:</span>
          <span className="font-normal"> {parts[1].trim()}</span>
        </>
      );
    } else {
      label = <span className="font-semibold">{formattedLabel}</span>;
    }
  } else {
    label = (
      <>
        <span className="font-semibold">{props.label}:</span>
        {selectedOption && (
          <>
            <span className="font-normal"> {selectedOption.label}</span>
          </>
        )}
      </>
    );
  }

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    props.onChange(newValue);
  };

  return (
    <SelectedFilter
      value={value}
      label={label}
      icon={props.icon}
      onChange={props.onChange}
      onRemove={props.onRemove ? () => props.onRemove!("") : undefined}
      hasValue={Boolean(value)}
      autoCloseOnChange={true}
      renderEditing={() => (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-neutral-700 block">
            {props.label}
          </label>
          <Select value={value || undefined} onValueChange={handleValueChange}>
            <SelectTrigger className="w-[250px] h-7 text-xs text-neutral-700 focus:border-neutral-400 data-[state=open]:border-neutral-400">
              <SelectValue
                placeholder={props.placeholder || `Select ${props.label}`}
                className="text-neutral-900"
              />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {props.options.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    className="text-xs text-neutral-700"
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      )}
    />
  );
}

export function ButtonSelectFilter(
  props: FilterProps<string | null> & {
    options: Array<{
      label: string;
      value: string | null;
    }>;
  }
) {
  const [value, setValue] = useState(props.value);
  const emptyValue = props.options.some((opt) => opt.value === null)
    ? null
    : "";

  const selectedOption = props.options.find((opt) => opt.value === value);

  let label: React.ReactNode;
  if (props.formatLabel) {
    const formattedLabel = props.formatLabel(value);
    const parts = formattedLabel.split(":");
    if (parts.length === 2) {
      label = (
        <>
          <span className="font-semibold">{parts[0].trim()}:</span>
          <span className="font-normal"> {parts[1].trim()}</span>
        </>
      );
    } else {
      label = <span className="font-semibold">{formattedLabel}</span>;
    }
  } else {
    label = (
      <>
        <span className="font-semibold">{props.label}:</span>
        {selectedOption && (
          <span className="font-normal"> {selectedOption.label}</span>
        )}
      </>
    );
  }

  const handleSelect = (newValue: string | null) => {
    setValue(newValue);
    props.onChange(newValue);
  };

  return (
    <SelectedFilter
      value={value}
      label={label}
      icon={props.icon}
      onChange={props.onChange}
      onRemove={props.onRemove ? () => props.onRemove!(emptyValue) : undefined}
      hasValue={Boolean(value)}
      autoCloseOnChange={true}
      renderEditing={() => (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-neutral-700 block">
            {props.label}
          </label>
          <div className="flex flex-col gap-1.5">
            {props.options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelect(opt.value)}
                className={cn(
                  "px-3 py-2 text-xs font-medium rounded-md transition-all text-left",
                  value === opt.value
                    ? "bg-blue-50 text-blue-600 border border-blue-200"
                    : "bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    />
  );
}

export function TagsFilter(props: FilterProps<string[]> & { tags: string[] }) {
  const [value, setValue] = useState(props.value);

  const label = (
    <>
      <span className="font-semibold">{props.label}:</span>
      {props.value.length > 0 && (
        <>
          <span className="font-normal"> {props.value.join(", ")}</span>
        </>
      )}
    </>
  );

  return (
    <SelectedFilter
      value={value}
      onChange={props.onChange}
      isValueEmpty={(value) => value.length === 0}
      label={label}
      icon={props.icon}
      onRemove={props.onRemove ? () => props.onRemove!([]) : undefined}
      hasValue={Boolean(value.length)}
      renderEditing={() => (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-neutral-700 block">
            {props.label}
          </label>
          <MultiSelectTags
            options={props.tags}
            value={value}
            onChange={setValue}
            placeholder={props.placeholder}
          />
        </div>
      )}
    />
  );
}

export function CheckBoxFilter(props: FilterProps<boolean>) {
  // Auto-enable when first added
  useEffect(() => {
    if (!props.value) {
      props.onChange(true);
    }
  }, []);

  // Simple chip that can only be removed - no editing popup needed
  return (
    <div className="relative">
      <Chip
        onRemove={props.onRemove ? () => props.onRemove!(false) : undefined}
        icon={props.icon}
        className="max-w-[250px] whitespace-nowrap overflow-hidden text-ellipsis"
      >
        <span className="font-semibold">{props.label}</span>
      </Chip>
    </div>
  );
}
